import React from 'react'
import PropTypes from 'prop-types'
import trackGTM from 'utils/trackGTM'
import VideoPlayer from 'components/VideoPlayer'
import { FadeInUpAnimationWrapper } from 'styles/animation'
import {
  GridTitleTextCont,
  GridTitle,
  GridSubtitle,
  GridWrapper,
  CalloutContainer,
  CalloutImage,
  VideoContainer,
  CalloutProductTextCont,
  CalloutProductTitle,
  CalloutProductCaption,
  Container,
  SubTitleContainer,
} from './GiftGuideCategory.style'

import GiftGuideProductCard from '../GiftGuideProductCard'
import CarouselItemModal from '../CarouselItemModal'

const GiftGuideCategory = ({
  id,
  title,
  subtitle,
  callouts,
  products,
  enableImageFadeIn,
  villageSlug,
}) => {
  const productModalRef = React.useRef()
  const calloutRef = React.useRef()
  const [screenWidth, setScreenWidth] = React.useState(null)

  React.useEffect(() => {
    if (calloutRef?.current?.offsetWidth) {
      setScreenWidth(calloutRef.current.offsetWidth)
    }
  }, [calloutRef])

  // Wrap calculate position func in a useMemo hook
  const calculatePositions = () => {
    return callouts.map((item) => {
      let columnData = { start: 1, end: 3 }
      const rowData = {
        start: item.desktopRow,
        end: item.desktopRow + 2,
      }

      if (item.desktopPosition.toUpperCase() === 'RIGHT')
        columnData = { start: 3, end: 5 }

      return (
        <CalloutContainer
          colStart={columnData.start}
          colEnd={columnData.end}
          rowStart={rowData.start}
          rowEnd={rowData.end}
          mobileRow={item.mobileRow}
          ref={calloutRef}>
          {item.image.__typename === 'ContentfulCompProductPrd01' ? (
            <FadeInUpAnimationWrapper enabled={enableImageFadeIn}>
              <GiftGuideProductCard
                isCallout
                {...item.product}
                onclickProductModal={() => {
                  trackGTM(
                    'Gift Guides',
                    'gift product card',
                    item.brand,
                    item.description
                  )
                  productModalRef.current.openModal({ ...item.product })
                }}
                villageSlug={villageSlug}
              />
            </FadeInUpAnimationWrapper>
          ) : (
            <FadeInUpAnimationWrapper enabled={enableImageFadeIn}>
              {item?.image?.portrait || item?.image?.landscape ? (
                <CalloutImage
                  image={item?.image?.portrait?.gatsbyImageData}
                  width={screenWidth}
                />
              ) : (
                <VideoContainer width={screenWidth}>
                  <VideoPlayer
                    controls={false}
                    name={item?.image?.name}
                    loop
                    muted
                    video={{
                      portrait: item?.image?.videoPortrait,
                      landscape: item?.image?.videoLandscape,
                    }}
                    autoplay
                    image={
                      item?.image?.videoPlaceholder?.portrait?.gatsbyImageData
                    }
                    videoMode="portrait"
                    villageSlug={villageSlug}
                  />
                </VideoContainer>
              )}

              <CalloutProductTextCont>
                <CalloutProductCaption>{item.caption}</CalloutProductCaption>
                <CalloutProductTitle>{item.title}</CalloutProductTitle>
              </CalloutProductTextCont>
            </FadeInUpAnimationWrapper>
          )}
        </CalloutContainer>
      )
    })
  }

  return (
    <Container name={id}>
      <GridTitleTextCont>
        <GridTitle>{title}</GridTitle>
        <SubTitleContainer>
          <GridSubtitle>{subtitle}</GridSubtitle>
        </SubTitleContainer>
      </GridTitleTextCont>
      <GridWrapper>
        {callouts != null && callouts.length > 0 && calculatePositions()}
        {products.map((product) => (
          <FadeInUpAnimationWrapper enabled={enableImageFadeIn}>
            <GiftGuideProductCard
              {...product}
              onclickProductModal={() => {
                trackGTM(
                  'Gift Guides',
                  'gift product card',
                  product.brand,
                  product.description
                )
                productModalRef.current.openModal({ ...product })
              }}
            />
          </FadeInUpAnimationWrapper>
        ))}
        <CarouselItemModal ref={productModalRef} />
      </GridWrapper>
    </Container>
  )
}

GiftGuideCategory.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  callouts: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object]))
    .isRequired,
  products: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object]))
    .isRequired,
  enableImageFadeIn: PropTypes.bool,
  villageSlug: PropTypes.string,
}

GiftGuideCategory.defaultProps = {
  enableImageFadeIn: false,
  villageSlug: '',
}

export default GiftGuideCategory
